/**
 * DocumentSearch Component
 * Advanced document search with filters for equipment association, type, status, and more
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Typography,
  Button,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Pagination,
  Tooltip,
  CircularProgress,
  Alert,
  Collapse,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  documentsService,
  Document,
  DocumentCategory,
  DocumentType,
  DocumentSearchParams,
  PaginatedResponse
} from '../../services/documentsService';
import { useSiteContext } from '../../hooks/useSiteContext';

interface DocumentSearchProps {
  equipmentId?: number;
  onDocumentSelect?: (document: Document) => void;
  showEquipmentFilter?: boolean;
  title?: string;
}

const DocumentSearch: React.FC<DocumentSearchProps> = ({
  equipmentId,
  onDocumentSelect,
  showEquipmentFilter = true,
  title = 'Document Search'
}) => {
  const { currentSite } = useSiteContext();
  
  // State
  const [searchParams, setSearchParams] = useState<DocumentSearchParams>({
    site: currentSite.siteId || undefined,
    equipment: equipmentId,
    page: 1,
    page_size: 20,
    ordering: '-created_date'
  });
  
  const [documents, setDocuments] = useState<PaginatedResponse<Document>>({
    count: 0,
    next: null,
    previous: null,
    results: []
  });
  
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Search when parameters change
  useEffect(() => {
    searchDocuments();
  }, [searchParams]);

  const loadInitialData = async () => {
    try {
      const [categoriesData, typesData] = await Promise.all([
        documentsService.getDocumentCategories(),
        documentsService.getDocumentTypes()
      ]);
      
      // Ensure we always have arrays, even if API returns null/undefined
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setDocumentTypes(Array.isArray(typesData) ? typesData : []);
    } catch (err) {
      console.error('Failed to load initial data:', err);
      setError('Document system is starting up. Categories and types will be available shortly.');
      // Set empty arrays on error
      setCategories([]);
      setDocumentTypes([]);
    }
  };

  const searchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await documentsService.getDocuments(searchParams);
      // Ensure we always have a proper response structure
      setDocuments(response || {
        count: 0,
        next: null,
        previous: null,
        results: []
      });
    } catch (err) {
      console.error('Failed to search documents:', err);
      setError('Document system not yet available. This feature will be available once document data is populated.');
      // Set empty results on error
      setDocuments({
        count: 0,
        next: null,
        previous: null,
        results: []
      });
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const handleSearchChange = (field: keyof DocumentSearchParams, value: any) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when search changes
    }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setSearchParams(prev => ({ ...prev, page: value }));
  };

  const clearFilters = () => {
    setSearchParams({
      site: currentSite.siteId || undefined,
      equipment: equipmentId,
      page: 1,
      page_size: 20,
      ordering: '-created_date'
    });
  };

  const downloadDocument = async (doc: Document) => {
    try {
      const blob = await documentsService.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.document_number}_${doc.title}.${doc.file_extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download document:', err);
      setError('Failed to download document');
    }
  };

  const getFileIcon = (extension: string) => {
    switch (extension.toLowerCase()) {
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'doc':
      case 'docx':
        return <DocumentIcon color="primary" />;
      default:
        return <FileIcon color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'approved':
        return 'info';
      case 'under_review':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchParams.search) count++;
    if (searchParams.document_type) count++;
    if (searchParams.category) count++;
    if (searchParams.status) count++;
    if (searchParams.access_level) count++;
    if (searchParams.date_from) count++;
    if (searchParams.date_to) count++;
    if (searchParams.expired) count++;
    if (searchParams.needs_review) count++;
    if (searchParams.file_extension) count++;
    return count;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" gutterBottom>
          {title}
          {documents.count > 0 && (
            <Chip 
              label={`${documents.count} documents`} 
              size="small" 
              sx={{ ml: 2 }} 
            />
          )}
        </Typography>

        {/* Search Bar */}
        <Card sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search documents by title, description, or keywords..."
                value={searchParams.search || ''}
                onChange={(e) => handleSearchChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchParams.search && (
                    <IconButton 
                      size="small" 
                      onClick={() => handleSearchChange('search', '')}
                    >
                      <ClearIcon />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={searchParams.document_type || ''}
                  label="Document Type"
                  onChange={(e) => handleSearchChange('document_type', e.target.value || undefined)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {(documentTypes || []).map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name} ({type.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={
                    <Badge badgeContent={getActiveFiltersCount()} color="primary">
                      <FilterIcon />
                    </Badge>
                  }
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                >
                  Filters
                  {showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Button>
                
                {getActiveFiltersCount() > 0 && (
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Advanced Filters */}
          <Collapse in={showAdvancedFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={searchParams.category || ''}
                      label="Category"
                      onChange={(e) => handleSearchChange('category', e.target.value || undefined)}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {(categories || []).map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={searchParams.status || ''}
                      label="Status"
                      onChange={(e) => handleSearchChange('status', e.target.value || undefined)}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="under_review">Under Review</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="published">Published</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                      <MenuItem value="obsolete">Obsolete</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select
                      value={searchParams.access_level || ''}
                      label="Access Level"
                      onChange={(e) => handleSearchChange('access_level', e.target.value || undefined)}
                    >
                      <MenuItem value="">All Levels</MenuItem>
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="restricted">Restricted</MenuItem>
                      <MenuItem value="confidential">Confidential</MenuItem>
                      <MenuItem value="classified">Classified</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="File Extension"
                    placeholder="e.g., pdf, doc, xls"
                    value={searchParams.file_extension || ''}
                    onChange={(e) => handleSearchChange('file_extension', e.target.value || undefined)}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Created From"
                    value={searchParams.date_from ? new Date(searchParams.date_from) : null}
                    onChange={(date) => handleSearchChange('date_from', date?.toISOString().split('T')[0])}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Created To"
                    value={searchParams.date_to ? new Date(searchParams.date_to) : null}
                    onChange={(date) => handleSearchChange('date_to', date?.toISOString().split('T')[0])}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Equipment</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : documents.results.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                        No documents found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.results.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFileIcon(doc.file_extension)}
                          <Box>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              sx={{ 
                                cursor: 'pointer',
                                color: 'primary.main',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => onDocumentSelect?.(doc)}
                            >
                              {doc.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doc.document_number}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {doc.document_type_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {doc.category_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={doc.status} 
                          size="small" 
                          color={getStatusColor(doc.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{doc.version}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(doc.created_date).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          by {doc.created_by_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {doc.association_display}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => onDocumentSelect?.(doc)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => downloadDocument(doc)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {documents.count > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={Math.ceil(documents.count / (searchParams.page_size || 20))}
                page={searchParams.page || 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default DocumentSearch; 